import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/outline';

/**
 * Single dialog shell for the app. Standardizes backdrop, panel surface,
 * title and close affordance. Variants (Modal, ConfirmDialog) compose this
 * instead of re-implementing Headless UI plumbing.
 *
 * @param {boolean} open
 * @param {function} onClose
 * @param {node|string} title - rendered inside DialogTitle (optional)
 * @param {string} titleAs - heading element for the title (default 'h3')
 * @param {boolean} showClose - top-right X button (default true)
 * @param {string} maxWidthClass - panel width (default 'max-w-lg')
 * @param {string} panelClassName - extra classes for the panel
 * @param {node} children - panel content below the title
 * @param {string} zIndexClass - stacking context (default 'z-[60]')
 */
export default function BaseDialog({
  open,
  onClose,
  title = null,
  titleAs = 'h3',
  showClose = true,
  maxWidthClass = 'max-w-lg',
  panelClassName = '',
  zIndexClass = 'z-[60]',
  children,
}) {
  return (
    <Dialog open={open} onClose={onClose} className={`relative ${zIndexClass}`}>
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-black/30 dark:bg-black/60 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in"
      />
      <div className="fixed inset-0 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
          <DialogPanel
            transition
            className={`relative w-full transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-200 data-[leave]:duration-150 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:data-[closed]:translate-y-0 sm:data-[closed]:scale-95 dark:bg-gray-870 dark:border dark:border-gray-700 dark:text-gray-100 ${maxWidthClass} ${panelClassName}`}
          >
            {showClose && (
              <div className="absolute right-0 top-0 z-10 pr-4 pt-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 dark:text-gray-300 dark:hover:text-white"
                >
                  <span className="sr-only">Close</span>
                  <XMarkIcon aria-hidden="true" className="h-6 w-6" />
                </button>
              </div>
            )}
            {title && (
              <DialogTitle
                as={titleAs}
                className="px-6 pt-5 text-base font-semibold text-gray-900 dark:text-gray-100"
              >
                {title}
              </DialogTitle>
            )}
            {children}
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
